/**
 * What a call that did not work can be asked about itself, as arithmetic.
 *
 * A failed call is the one failure in this app that leaves nothing anywhere.
 * The server relays signalling and never sees the media, so from its side a call
 * that connected and a call that spent thirty seconds finding no path through
 * look identical — both are a `call:invite`, some opaque bytes, and a hang-up.
 * The only witness is the browser, and the only thing it knows is in
 * `RTCPeerConnection`: which candidates it gathered, which ICE server refused
 * it, and which pair, if any, ended up carrying the audio.
 *
 * Everything here is pure and works on plain objects, so the rules that decide
 * what is worth a row can be tested without a peer connection. The half that
 * touches WebRTC is lib/webrtc.ts, and the half that knows which call it was is
 * calls/CallContext.tsx.
 *
 * The vocabulary is the standard's and is left in English on purpose: these
 * lines are read out of `client_errors` with a SQL client, next to a candidate
 * type and an ICE error code that are spelled that way in every other document
 * about them.
 */

/** One note, in the shape reportClientError takes. */
export interface CallNote {
  detail: string;
  message: string;
}

/**
 * How a candidate reached the network, in the standard's words. `host` is this
 * machine's own address, `srflx` is what STUN said the outside sees, `relay` is
 * an allocation on the TURN server — the only one that survives two networks
 * that refuse to talk to each other directly.
 */
export interface CandidateTally {
  host: number;
  srflx: number;
  prflx: number;
  relay: number;
}

export function tallyCandidates(types: readonly (string | null | undefined)[]): CandidateTally {
  const tally: CandidateTally = { host: 0, srflx: 0, prflx: 0, relay: 0 };
  for (const type of types) {
    if (type === 'host' || type === 'srflx' || type === 'prflx' || type === 'relay') {
      tally[type] += 1;
    }
  }
  return tally;
}

/** `host 2 · srflx 1 · relay 0`. Zeroes are kept — a missing relay is the finding. */
export function describeTally(tally: CandidateTally): string {
  return `host ${tally.host} · srflx ${tally.srflx} · relay ${tally.relay}`;
}

/**
 * The fields this module reads off an `RTCStats` entry. Everything is optional
 * because which of them exist depends on the entry's `type`, and because
 * browsers disagree about several of them — Firefox has no `selected` on a
 * candidate pair, Safari has had no `relayProtocol`.
 */
export interface StatEntry {
  id?: string;
  type?: string;
  /** candidate-pair; the byte counts also appear on inbound-/outbound-rtp. */
  state?: string;
  nominated?: boolean;
  selected?: boolean;
  localCandidateId?: string;
  remoteCandidateId?: string;
  bytesReceived?: number;
  bytesSent?: number;
  /** transport */
  selectedCandidatePairId?: string;
  /** local-candidate / remote-candidate */
  candidateType?: string;
  protocol?: string;
  relayProtocol?: string;
  /**
   * Which ICE server produced this candidate — the url out of the list
   * /api/calls/ice handed over, verbatim.
   *
   * It is the only place the *name* survives. The relay answers several of
   * them on one address (turn.<domain> and the IPv4-only turn4.<domain>), so
   * neither coturn's log nor anything else about the connection can say which
   * door a call came through; without this, «it relayed over TLS» is as close
   * as the question can be answered. Local candidates only, and absent on
   * host ones, which came from no server at all.
   */
  url?: string;
  /** candidate-pair, again — what the congestion controller thinks the path holds. */
  availableOutgoingBitrate?: number;
  /** Seconds, not milliseconds. The standard is unusual here and it is easy to print wrong. */
  currentRoundTripTime?: number;
  /**
   * outbound-rtp / inbound-rtp. `kind` is the current spelling and `mediaType`
   * the one Safari still answers with, which matters more here than usual: one
   * end of every call this app makes is Safari.
   */
  kind?: string;
  mediaType?: string;
  codecId?: string;
  frameWidth?: number;
  frameHeight?: number;
  framesPerSecond?: number;
  /**
   * The video `media-source`: the picture as the camera handed it over, before
   * the encoder had any say in what to do with it.
   *
   * A different pair of names from the `frameWidth`/`frameHeight` above, and
   * that is the standard's doing rather than a choice here — a source measures
   * `width`/`height`, a stream measures the long pair, and asking a source for
   * the long ones answers undefined with no error to say why. Only
   * `framesPerSecond` is shared, which is the field that matters most: the
   * camera's own rate beside the encoder's is what separates a camera that
   * stopped from a camera still feeding the same frame over and over.
   */
  width?: number;
  height?: number;
  /** outbound-rtp: what the encoder is short of *right now*. See limitation(). */
  qualityLimitationReason?: string;
  /** outbound-rtp: seconds spent short of each thing, cumulative over the call. */
  qualityLimitationDurations?: Record<string, number>;
  /** inbound-rtp: seconds, like every other duration the standard hands out. */
  jitter?: number;
  /**
   * How loud the voice actually is, which no other field here says.
   *
   * Every byte counter can look perfect on a call somebody can barely hear:
   * Opus spends much the same bitrate on a whisper as on speech, so «звук был
   * очень тихий» leaves no trace in audioInBps at all. `totalAudioEnergy` and
   * `totalSamplesDuration` are cumulative and give an honest RMS over an
   * interval — see voiceLevel in callStats.ts — where `audioLevel` alone is one
   * 100 ms window that lands in a pause between words as often as not.
   *
   * On inbound-rtp they describe what arrived; on the audio `media-source` they
   * describe what this device's own microphone captured, which is the half that
   * separates «их микрофон тихий» from «у нас тихо играет».
   */
  audioLevel?: number;
  totalAudioEnergy?: number;
  totalSamplesDuration?: number;
  /** inbound-rtp */
  freezeCount?: number;
  totalFreezesDuration?: number;
  packetsLost?: number;
  packetsReceived?: number;
  /**
   * outbound-rtp / inbound-rtp, and the only fields here that name software
   * rather than measure it: whether this device encoded in hardware is the
   * difference between «the phone is hot» and «the network is bad», and no
   * number in the sample says it. Carried in the sample's `extra`, one call at
   * a time, rather than given a column nobody would group by.
   */
  encoderImplementation?: string;
  decoderImplementation?: string;
  /** How much of the picture had to be asked for again, cumulative. */
  nackCount?: number;
  pliCount?: number;
  /** codec */
  mimeType?: string;
}

export interface ConnectionSummary {
  local: CandidateTally;
  remote: CandidateTally;
  /**
   * How the media actually got through, in four parts, the last two only when
   * a relay was involved: `relay/srflx udp via tls from turn4.makaek.com` is
   * media over UDP to a peer's reflexive address, through a relay this device
   * could only reach over TLS, by the IPv4-only name. Null when nothing won.
   */
  route: string | null;
  /** Bytes that arrived over the winning pair. 0 with a route is a half-open call. */
  bytesReceived: number | null;
}

/**
 * The pair that won, if one did.
 *
 * Three ways of asking, because the standard's answer and the browsers' answers
 * are not the same age: `transport.selectedCandidatePairId` is the current one,
 * `selected` on the pair is Chrome's older spelling, and a nominated pair in
 * state `succeeded` is what is left when neither is present. A connection that
 * never came up answers none of them, which is the case this exists to tell
 * apart from the rest.
 */
export function selectedPair(stats: readonly StatEntry[]): StatEntry | null {
  const transport = stats.find(
    (entry) => entry.type === 'transport' && typeof entry.selectedCandidatePairId === 'string',
  );
  if (transport) {
    const byId = stats.find((entry) => entry.id === transport.selectedCandidatePairId);
    if (byId) return byId;
  }
  return (
    stats.find((entry) => entry.type === 'candidate-pair' && entry.selected === true) ??
    stats.find(
      (entry) =>
        entry.type === 'candidate-pair' && entry.nominated === true && entry.state === 'succeeded',
    ) ??
    null
  );
}

function candidateType(stats: readonly StatEntry[], id: string | undefined): string {
  if (id === undefined) return '?';
  return stats.find((entry) => entry.id === id)?.candidateType ?? '?';
}

export function summariseConnection(stats: readonly StatEntry[]): ConnectionSummary {
  const local = tallyCandidates(
    stats.filter((entry) => entry.type === 'local-candidate').map((entry) => entry.candidateType),
  );
  const remote = tallyCandidates(
    stats.filter((entry) => entry.type === 'remote-candidate').map((entry) => entry.candidateType),
  );

  const pair = selectedPair(stats);
  if (!pair) return { local, remote, route: null, bytesReceived: null };

  const localType = candidateType(stats, pair.localCandidateId);
  const remoteType = candidateType(stats, pair.remoteCandidateId);
  const localStat = stats.find((entry) => entry.id === pair.localCandidateId);
  // How the browser reached the relay, which is a different question from what
  // the pair itself speaks: `relay/... udp via tcp` is media over UDP through a
  // relay this device could only get to over TCP, and that is the shape of a
  // network where everything but 443 is filtered.
  const transport = localStat?.relayProtocol ?? '';
  // And which of the relay's doors it came through. One address answers to
  // several names, so this is the only record of the one a call actually used
  // — see `url` on StatEntry. The host alone: the scheme and transport are
  // already said above, and the whole url would push `route` past the width
  // anything reads it in.
  const door = localType === 'relay' ? hostOfUrl(localStat?.url) : '';

  return {
    local,
    remote,
    route: `${localType}/${remoteType} ${localStat?.protocol ?? '?'}${transport ? ` via ${transport}` : ''}${door ? ` from ${door}` : ''}`,
    bytesReceived: typeof pair.bytesReceived === 'number' ? pair.bytesReceived : null,
  };
}

/**
 * The host out of an ICE server url, or '' when there is nothing to take.
 *
 * `turns:turn4.makaek.com:443?transport=tcp` → `turn4.makaek.com`. Deliberately
 * forgiving: this only ever decorates a diagnostic line, so a url shaped in a
 * way no browser has produced yet costs the decoration and nothing else.
 */
function hostOfUrl(url: string | undefined): string {
  if (!url) return '';
  const afterScheme = url.slice(url.indexOf(':') + 1);
  return afterScheme.split(/[:?]/)[0] ?? '';
}

/** One line: what was gathered on both sides, and what — if anything — won. */
export function describeConnection(summary: ConnectionSummary): string {
  return [
    `route ${summary.route ?? 'none'}`,
    `local ${describeTally(summary.local)}`,
    `remote ${describeTally(summary.remote)}`,
    summary.bytesReceived === null ? 'in ?' : `in ${summary.bytesReceived}B`,
  ].join(' · ');
}

/**
 * What the picture cost, as opposed to whether there was one.
 *
 * Everything above this line answers «did it connect», which is the question a
 * call that fails asks. A call that connects and looks bad asks a different one,
 * and the answer is never in the candidates: it is in what the encoder chose,
 * what it was short of while choosing, and what arrived at the far end. Those
 * are three separate places in `getStats()` and one line here.
 */
export interface MediaSummary {
  /** `H264`, `VP8` — the negotiated video codec. Null on an audio call. */
  codec: string | null;
  /** `640×480@24`, what this side is sending. Null before the first frame. */
  sending: string | null;
  /** The same for what is arriving. */
  receiving: string | null;
  /** What the encoder was mostly short of: `bandwidth`, `cpu`, `other`. Null when nothing. */
  limit: string | null;
  /** How much of the call it was short of anything at all, 0–1. Null when unmeasurable. */
  limitedShare: number | null;
  /** Bits per second the congestion controller believes the path will carry. */
  availableBps: number | null;
  /** Round trip over the winning pair, in milliseconds. */
  rttMs: number | null;
  /** Frozen picture at this end: how many, and how long in total. */
  freezes: { count: number; seconds: number } | null;
  /** Share of inbound packets that never arrived, 0–1. */
  loss: number | null;
  /**
   * Bytes of sound sent and received. Here because a dead direction was being
   * inferred rather than measured: a phone whose video arrives at one frame a
   * second may or may not still be delivering audio, and these two numbers are
   * the difference between a bad picture and a media path that moved nothing.
   */
  audioOut: number | null;
  audioIn: number | null;
  /**
   * And how loud those bytes were, 0..1 RMS over the whole call — the one
   * question the byte counts cannot answer, because Opus spends much the same
   * bitrate on a whisper as on speech. `voiceOut` is this device's microphone,
   * `voiceIn` what arrived; a call somebody could barely hear has one of them
   * near zero and every other figure on the line healthy.
   */
  voiceOut: number | null;
  voiceIn: number | null;
  /**
   * Bytes of picture this side sent, which `sending` cannot stand in for: an
   * encoder that was never handed a frame reports no `frameWidth`, so that
   * field simply disappears from the line and reads as «not measured» next to
   * an `in 480×640@20` sitting right beside it. It was measured. It was zero.
   */
  videoOut: number | null;
}

/** Safari's spelling and everyone else's, in one place. */
export function streamKind(entry: StatEntry): string | undefined {
  return entry.kind ?? entry.mediaType;
}

function frameSize(entry: StatEntry | undefined): string | null {
  if (!entry?.frameWidth || !entry.frameHeight) return null;
  const fps = entry.framesPerSecond;
  const rate = typeof fps === 'number' ? `@${Math.round(fps)}` : '';
  return `${entry.frameWidth}×${entry.frameHeight}${rate}`;
}

/** `video/H264` → `H264`. The half before the slash is the kind, which is known. */
function codecName(stats: readonly StatEntry[], id: string | undefined): string | null {
  if (id === undefined) return null;
  const mime = stats.find((entry) => entry.id === id)?.mimeType;
  if (mime === undefined) return null;
  return mime.split('/')[1] ?? mime;
}

/**
 * What the encoder was short of, over the whole call rather than in the instant
 * it was asked.
 *
 * `qualityLimitationReason` on its own is very close to useless here: the
 * encoder changes its mind several times a second, and a call that spent most
 * of its life starved of bandwidth reads `none` if the last frame before the
 * hang-up happened to fit. `qualityLimitationDurations` is the same fact
 * integrated — cumulative seconds per cause — and it is what turns one reading
 * into an account of the call. The instantaneous field is the fallback for
 * browsers that do not keep the durations.
 */
export function limitation(entry: StatEntry | undefined): { reason: string | null; share: number | null } {
  const durations = entry?.qualityLimitationDurations;
  if (durations) {
    let total = 0;
    let limited = 0;
    let worst: string | null = null;
    let worstSeconds = 0;
    for (const [reason, seconds] of Object.entries(durations)) {
      if (typeof seconds !== 'number' || seconds <= 0) continue;
      total += seconds;
      if (reason === 'none') continue;
      limited += seconds;
      if (seconds > worstSeconds) {
        worst = reason;
        worstSeconds = seconds;
      }
    }
    if (total > 0) return { reason: worst, share: limited / total };
  }
  const reason = entry?.qualityLimitationReason;
  // `none` is not a limit, and reporting it as one would make every healthy call
  // look like a finding. It survives as `share: 0` when the durations exist.
  return { reason: reason === undefined || reason === 'none' ? null : reason, share: null };
}

/**
 * The RMS of one direction over a whole call, 0..1.
 *
 * No differencing here, unlike the per-interval figure in callStats.ts: this is
 * read once, at the end, and the cumulative pair *is* the whole call. Which is
 * also why it is worth having on the closing line — one number that says how
 * loud a call was, on the row somebody reads when they are told it was quiet.
 */
function wholeCallLevel(entry: StatEntry | undefined): number | null {
  const energy = entry?.totalAudioEnergy;
  const seconds = entry?.totalSamplesDuration;
  if (typeof energy !== 'number' || typeof seconds !== 'number' || seconds <= 0) return null;
  return Math.round(Math.sqrt(energy / seconds) * 1000) / 1000;
}

export function summariseMedia(stats: readonly StatEntry[]): MediaSummary {
  const outbound = stats.find(
    (entry) => entry.type === 'outbound-rtp' && streamKind(entry) === 'video',
  );
  const inbound = stats.find(
    (entry) => entry.type === 'inbound-rtp' && streamKind(entry) === 'video',
  );
  const audioOutbound = stats.find(
    (entry) => entry.type === 'outbound-rtp' && streamKind(entry) === 'audio',
  );
  const audioInbound = stats.find(
    (entry) => entry.type === 'inbound-rtp' && streamKind(entry) === 'audio',
  );
  // The capture's own loudness, which no outbound-rtp reports: what left the
  // microphone before the encoder saw it.
  const audioSource = stats.find(
    (entry) => entry.type === 'media-source' && streamKind(entry) === 'audio',
  );
  const pair = selectedPair(stats);
  const { reason, share } = limitation(outbound);

  const received = inbound?.packetsReceived;
  const lost = inbound?.packetsLost;
  const arrived = (received ?? 0) + (lost ?? 0);

  return {
    // The sender's codec, because it is the one this side chose and the one a
    // `setCodecPreferences` would be arguing with. Both ends negotiate the same.
    codec: codecName(stats, outbound?.codecId),
    sending: frameSize(outbound),
    receiving: frameSize(inbound),
    limit: reason,
    limitedShare: share,
    availableBps: typeof pair?.availableOutgoingBitrate === 'number'
      ? Math.round(pair.availableOutgoingBitrate)
      : null,
    rttMs: typeof pair?.currentRoundTripTime === 'number'
      ? Math.round(pair.currentRoundTripTime * 1000)
      : null,
    freezes: typeof inbound?.freezeCount === 'number'
      ? { count: inbound.freezeCount, seconds: Math.round(inbound.totalFreezesDuration ?? 0) }
      : null,
    loss:
      typeof received === 'number' && typeof lost === 'number' && arrived > 0
        ? lost / arrived
        : null,
    audioOut: typeof audioOutbound?.bytesSent === 'number' ? audioOutbound.bytesSent : null,
    audioIn: typeof audioInbound?.bytesReceived === 'number' ? audioInbound.bytesReceived : null,
    voiceOut: wholeCallLevel(audioSource),
    voiceIn: wholeCallLevel(audioInbound),
    videoOut: typeof outbound?.bytesSent === 'number' ? outbound.bytesSent : null,
  };
}

/**
 * One line, or null when nothing here could be measured at all. Null rather than
 * an empty string so the caller drops the field instead of printing a stray
 * separator. An audio call keeps the round trip and the estimate, which are the
 * two that mean something without a picture.
 */
export function describeMedia(summary: MediaSummary): string | null {
  const limit =
    summary.limit === null
      ? // A measured zero is worth saying: it is the difference between «the
        // encoder was content» and «nothing here could tell».
        summary.limitedShare === 0
        ? 'limit none'
        : null
      : `limit ${summary.limit}${
          summary.limitedShare === null ? '' : ` ${Math.round(summary.limitedShare * 100)}%`
        }`;

  const parts = [
    summary.codec === null ? null : `codec ${summary.codec}`,
    summary.sending === null ? null : `out ${summary.sending}`,
    summary.receiving === null ? null : `in ${summary.receiving}`,
    // Zeroes survive, like the tallies': an audio direction that moved nothing
    // is the finding, and dropping it would read as «not measured».
    summary.videoOut === null ? null : `vout ${summary.videoOut}B`,
    summary.audioOut === null ? null : `aout ${summary.audioOut}B`,
    summary.audioIn === null ? null : `ain ${summary.audioIn}B`,
    // Percent of full scale, where ordinary speech sits around 5: the units a
    // person can compare the two directions in without stopping to think.
    summary.voiceOut === null ? null : `vox↑ ${Math.round(summary.voiceOut * 1000) / 10}%`,
    summary.voiceIn === null ? null : `vox↓ ${Math.round(summary.voiceIn * 1000) / 10}%`,
    limit,
    summary.availableBps === null ? null : `est ${Math.round(summary.availableBps / 1000)}kbps`,
    summary.rttMs === null ? null : `rtt ${summary.rttMs}ms`,
    summary.freezes === null || summary.freezes.count === 0
      ? null
      : `${summary.freezes.count} freezes ${summary.freezes.seconds}s`,
    summary.loss === null || summary.loss === 0 ? null : `loss ${(summary.loss * 100).toFixed(1)}%`,
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * How much of a call has to be spent short of something before it is a finding
 * rather than weather. A quarter: every video call on a real network spends a
 * few seconds limited while the estimator finds the ceiling, and filing those
 * would bury the calls that spent their whole length there.
 */
const LIMITED_SHARE_FLOOR = 0.25;

/** Loss the codec cannot conceal. Below this a viewer sees nothing; above it, smearing. */
const LOSS_FLOOR = 0.02;

/**
 * The note for a call that worked and looked bad, or null for one that did not.
 *
 * This is the third row this module knows how to file, and it exists because the
 * other two cannot see the case: a call that connects, carries video for a
 * minute and is hung up on normally is indistinguishable from a good one
 * everywhere else in this app, including to the person who had it — «плохо
 * слышно» and «плохо видно» arrive as opinions rather than as bytes.
 *
 * Deliberately silent on a healthy call, for the same reason `reportOutcome`
 * files nothing for an ordinary hang-up: a row per call is a log nobody reads.
 * What it takes to break the silence is a threshold, not a difference — an
 * encoder that was starved for a quarter of the call, a picture that actually
 * froze, or loss past what the codec hides.
 */
export function qualityNote(summary: MediaSummary): CallNote | null {
  const starved =
    summary.limit !== null &&
    (summary.limitedShare === null || summary.limitedShare >= LIMITED_SHARE_FLOOR);
  const froze = (summary.freezes?.count ?? 0) > 0;
  const lossy = (summary.loss ?? 0) >= LOSS_FLOOR;
  if (!starved && !froze && !lossy) return null;

  const line = describeMedia(summary);
  if (line === null) return null;
  return {
    // Grouped by cause rather than by call, so the throttle keeps one of each
    // kind rather than the first few of whichever happened this evening. Which
    // one is named when several are true is the one that explains the others:
    // starvation produces freezes, and freezes are not what caused the loss.
    detail: `call/quality/${starved ? summary.limit : froze ? 'freezes' : 'loss'}`,
    message: line,
  };
}

/** What the call was, so a row can be read without the call still existing. */
export interface CallFacts {
  direction: 'outgoing' | 'incoming';
  video: boolean;
  /**
   * Whether the server said it had a relay at all — see /api/calls/ice. Null
   * when the call failed before that answer arrived, which is a third thing and
   * not the same as "no relay": it is one of the ways a call fails.
   */
  relayConfigured: boolean | null;
  /** Where the call had got to when this happened. */
  phase: string;
  /** How long since the call opened. Says whether ICE was given time to fail. */
  ageMs: number;
  /**
   * Which connection carried it: the page's RTCPeerConnection or the iOS
   * shell's libwebrtc. Every other fact on a row is the same sentence from
   * either, so without this a `relay 0` read out of `client_errors` cannot
   * be attributed — and attributing one wrongly cost a build and a device
   * pass. Optional so a caller that predates the field reads as `page`,
   * which is what every platform but the iOS shell is.
   */
  native?: boolean;
  /**
   * Which app the person was in: `web`, `android`, `ios`. The other half of the
   * sentence `native` starts, and the half that was missing — the Android shell
   * carries a call on the page's own peer, so `via=page` there is not «в
   * браузере», it is «на web-пути», and a whole platform read as the browser
   * for as long as this was one field. Optional so an older caller reads as `?`
   * rather than as the web.
   */
  shell?: string;
}

/** `out · video · via=page · on=android · relay=yes · phase=active · 8s`. */
export function describeCall(facts: CallFacts): string {
  return [
    facts.direction === 'outgoing' ? 'out' : 'in',
    facts.video ? 'video' : 'audio',
    `via=${facts.native ? 'native' : 'page'}`,
    `on=${facts.shell ?? '?'}`,
    `relay=${facts.relayConfigured === null ? '?' : facts.relayConfigured ? 'yes' : 'no'}`,
    `phase=${facts.phase}`,
    `${Math.round(facts.ageMs / 1000)}s`,
  ].join(' · ');
}

/**
 * What an ICE error code means, for the three or four that are a deployment
 * problem rather than weather.
 *
 * 401 is the one worth knowing by sight: coturn computed a different HMAC from
 * the username it was handed, which means this server's `TURN_SECRET` and the
 * relay's `static-auth-secret` are not the same string — a relay that is up,
 * reachable, and refuses every allocation. 701 is the other end of it: nothing
 * answered at all, so the port is closed or the host is wrong.
 */
export function iceErrorName(code: number): string {
  if (code === 401) return 'turn credentials rejected';
  if (code === 403) return 'turn refused the allocation';
  if (code === 437) return 'allocation mismatch';
  if (code === 486) return 'turn out of ports';
  if (code === 508) return 'turn out of capacity';
  if (code >= 700) return 'ice server unreachable';
  return 'ice server error';
}

export interface IceErrorFacts {
  errorCode: number;
  errorText: string;
  /** The server that failed, as it was configured: `turn:host:3478?transport=udp`. */
  url: string;
}

/**
 * A note for one ICE server that would not serve this device, or null when the
 * error is part of how the protocol works.
 *
 * 438 is the whole of that exception. A TURN allocation is refreshed for as long
 * as the call lasts, and every refresh past the nonce's lifetime is answered
 * with 438 and retried with the new nonce by the browser itself — so recording
 * it would file a row every few minutes of a call that is working perfectly.
 *
 * Everything else is reported, including on a call that goes on to connect: a
 * relay that refuses this device is a fact about the deployment, and the call
 * connecting anyway only means the two ends happened to be on one network. That
 * is exactly the failure that hides until someone leaves the building.
 */
export function iceErrorNote(facts: IceErrorFacts): CallNote | null {
  if (facts.errorCode === 438) return null;
  return {
    // The code and not the url, so the throttle groups by what went wrong
    // rather than letting the udp and tcp entries of one dead relay count as
    // two different problems. The url is in the message.
    detail: `call/ice-server/${facts.errorCode}`,
    message: [iceErrorName(facts.errorCode), facts.url || 'no url', facts.errorText || '—'].join(
      ' · ',
    ),
  };
}

/**
 * The local address a srflx candidate was gathered from, out of its SDP.
 *
 * `raddr` on a srflx candidate is the base — the interface that reached a
 * STUN/TURN server, which is what iceErrorNotes judges errors against. The
 * browser hands the same fact over as `relatedAddress`; the native side only
 * has the SDP line, so it is read from there. Null for every other type: on
 * a relay candidate `raddr` is the reflexive address, and a public address in
 * the reached set would match no error's interface and quietly file them all.
 */
export function relatedAddressOf(candidate: string): string | null {
  const match = / typ srflx raddr (\S+)/.exec(candidate);
  return match?.[1] ?? null;
}

/** One ICE error, with the interface it was sent from. */
export interface IceErrorRecord extends IceErrorFacts {
  /**
   * The local address the request went out from, `null` when the browser did
   * not say. Chrome does; it is the whole basis of the filtering below.
   */
  address: string | null;
}

/**
 * One gathering pass's ICE errors, minus the ones that are about an interface
 * rather than about the relay.
 *
 * A browser gathers from every interface it can see, and a desktop has more of
 * them than its owner thinks: WSL and Hyper-V install one each, VPN clients
 * leave theirs behind. Nothing routes out of those, so every ICE server is
 * unreachable from them — as `Address not associated with the desired network
 * interface` for a url over TCP, and as a plain timeout for one over UDP — and
 * each failure is filed against a call that connected in two seconds over
 * Wi-Fi. Four urls and two dead adapters is eight rows saying nothing, which is
 * more than enough to bury the one row that means something.
 *
 * What separates the two is neither the code nor the address — a phone's real
 * interface is a private address too — but whether anything ever came back on
 * that address. `reachedFrom` holds the local bases that produced a srflx
 * candidate — an address missing from it reached no STUN server at all, so what
 * it has to say about any particular one is worthless.
 *
 * An empty `reachedFrom` drops nothing, and that is the point rather than a
 * degenerate case: no interface reached anything is precisely the fault this
 * layer exists to report, and it must not be filtered by its own symptom.
 */
export function iceErrorNotes(
  errors: readonly IceErrorRecord[],
  reachedFrom: ReadonlySet<string>,
): CallNote[] {
  const notes: CallNote[] = [];
  const seen = new Set<string>();
  for (const error of errors) {
    if (reachedFrom.size > 0 && error.address !== null && !reachedFrom.has(error.address)) continue;
    const note = iceErrorNote(error);
    if (note === null) continue;
    // One url failing from three interfaces is one finding. Grouped by the
    // message and not by the detail, which would fold the udp url into the
    // turns: one — two listeners, two different things to go and fix.
    const key = `${note.detail}|${note.message}`;
    if (seen.has(key)) continue;
    seen.add(key);
    notes.push(note);
  }
  return notes;
}

/**
 * The note for gathering having finished without a single relay candidate,
 * while this deployment has a relay.
 *
 * This is the check that catches the failure nobody notices. Two phones on one
 * Wi-Fi connect over their `host` candidates and never touch TURN, so a relay
 * that is misconfigured, firewalled or simply not running produces calls that
 * work perfectly — right up until the two ends are on different networks, which
 * is every call that matters. Gathering completing with `relay 0` says so at the
 * first call after the deployment rather than at the first call from a train.
 *
 * Null when the relay did allocate, and null when there is no relay configured:
 * that is a deployment choice the client already tells its user about, not a
 * fault to file a row for on every call.
 */
/**
 * One of this device's own tracks, in the fields that say whether it is
 * producing anything. Plain data rather than a `MediaStreamTrack` so the rules
 * below can be tested without a camera; the caller reads `getSettings()`.
 */
export interface LocalTrackFacts {
  kind: string;
  /** 'live' or 'ended'. A track the OS took away ends without anyone asking. */
  readyState: string;
  /**
   * The one field this whole census exists for. On a *local* track `muted` is
   * not a person pressing a button — it is the system holding the capture: an
   * app that is not in front, a camera another process took, an interruption.
   * A muted track resolves its promise, throws nothing, appears in the SDP and
   * sends silence, which is exactly the failure that leaves no other trace.
   */
  muted: boolean;
  /** The person's own mute and camera buttons. False is a choice, not a fault. */
  enabled: boolean;
  /** Video only, from getSettings(); absent on some browsers until a frame lands. */
  width?: number;
  height?: number;
  frameRate?: number;
}

/** What the capture was asked for in the middle of, so a row reads on its own. */
export interface LocalMediaFacts {
  /**
   * How the call got to the point of opening the camera: 'outgoing' is the
   * caller's own tap, 'page' the green button in an app that is plainly in
   * front, 'callkit' the system's call screen — which is the one case where
   * the WebView may be asking for capture while iOS still considers the app to
   * be in the background.
   */
  origin: string;
  /** document.visibilityState at that moment; the other half of the same question. */
  visibility: string;
  /**
   * Which reading this is: the instant getUserMedia resolved, and twice more
   * while the call runs. No single one of them is enough — a track suspended at
   * the first and live at the last is a race worth waiting out, one suspended
   * throughout is capture that is never coming — and the first pass measured
   * both devices taking twenty seconds to start, which is why there is a late
   * one at all.
   *
   * It is part of the detail rather than only of the message, because the
   * reporter throttles by detail: with one slug for all three, the first
   * reading silenced the two that say how it ended. See admit() in errorLog.ts.
   */
  at: string;
}

/** `audio live` · `video live muted 0×0` — everything one track can say. */
export function describeTrack(track: LocalTrackFacts): string {
  const size =
    track.kind === 'video'
      ? ` ${track.width ?? 0}×${track.height ?? 0}${
          typeof track.frameRate === 'number' ? `@${Math.round(track.frameRate)}` : ''
        }`
      : '';
  return `${track.kind} ${track.readyState}${track.muted ? ' muted' : ''}${
    track.enabled ? '' : ' disabled'
  }${size}`;
}

/**
 * The note for capture that opened and produced nothing, or null for capture
 * that is working.
 *
 * This is the one thing about a call this app could not see. Everything else
 * here asks about the path between two devices — candidates, relays, what
 * arrived — and all of it reads perfectly on a call where the local camera and
 * microphone were dead from the first second: ICE connects, the far end's video
 * plays, the timer runs, and `getUserMedia` neither threw nor warned. The only
 * witness is the state of the tracks themselves, and nothing was reading it.
 *
 * Silent when they are live and unmuted, like everything else in this file: a
 * row per call is a log nobody reads. What breaks the silence is a track that
 * the system suspended, one it ended, or a picture with no size — never a
 * person's own mute button, which is `enabled` and is a choice.
 */
export function describeLocalMedia(
  tracks: readonly LocalTrackFacts[],
  facts: LocalMediaFacts,
): string {
  const census = tracks.length === 0 ? 'no tracks' : tracks.map(describeTrack).join(' · ');
  return [`at ${facts.at}`, `answer ${facts.origin}`, `page ${facts.visibility}`, census].join(' · ');
}

export function localMediaNote(
  tracks: readonly LocalTrackFacts[],
  facts: LocalMediaFacts,
): CallNote | null {
  const line = describeLocalMedia(tracks, facts);

  // In the order that explains the others: a track that is gone was not
  // suspended, and a suspended camera is why the picture has no size.
  const cause =
    tracks.length === 0
      ? 'none'
      : tracks.some((track) => track.readyState !== 'live')
        ? 'ended'
        : tracks.some((track) => track.muted)
          ? 'muted'
          : tracks.some(
                (track) => track.kind === 'video' && !(track.width && track.height),
              )
            ? 'dark'
            : null;
  if (cause === null) return null;

  return { detail: `call/local/${cause}/${facts.at}`, message: line };
}

/**
 * The note for a microphone that is live, unmuted, not muted by its owner — and
 * whose sender has still put nothing on the wire.
 *
 * The state two device passes ended in, and the one localMediaNote is blind to
 * by construction: every track reads healthy, so the census says nothing, and
 * the only witness is a byte count on the other side of the encoder. Both
 * passes had it on the *caller* and only the caller, on either device, which is
 * how the audio session turned out to be the suspect rather than the sender.
 *
 * `null` bytes is «could not ask» and is not a finding; anything above zero is
 * a working microphone. Only a measured zero, against a track that says it is
 * producing, is worth a row.
 */
export function silentSenderNote(
  tracks: readonly LocalTrackFacts[],
  facts: LocalMediaFacts,
  audioBytesSent: number | null,
): CallNote | null {
  if (audioBytesSent !== 0) return null;
  const producing = tracks.some(
    (track) =>
      track.kind === 'audio' && track.readyState === 'live' && !track.muted && track.enabled,
  );
  if (!producing) return null;
  return {
    detail: `call/local/silent-sender/${facts.at}`,
    message: `microphone live, sender sent 0B · ${describeLocalMedia(tracks, facts)}`,
  };
}

export function gatheringNote(
  tally: CandidateTally,
  relayConfigured: boolean,
  iceErrors: number,
): CallNote | null {
  if (!relayConfigured || tally.relay > 0) return null;
  return {
    detail: 'call/gathering/no-relay',
    message: [
      'gathering finished with no relay candidate',
      describeTally(tally),
      // Whether an ICE server said why is the first thing to know: with an error
      // the code names the problem, and without one the packets went nowhere,
      // which points at the firewall rather than at coturn.
      iceErrors > 0 ? `${iceErrors} ice server errors` : 'no ice server errors',
    ].join(' · '),
  };
}
